import { getHubBrand } from '@/store';
import { clearBrandSignedOut } from './brandSignOut';
import { AUTH_TOKEN_CHANGED_EVENT, type LoginMethod, STORAGE_KEYS } from './constants';

// gloss
const getItem = (key: string): string | null => {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(key);
};

const setItem = (key: string, value: string) => {
  if (typeof window === 'undefined') return;
  localStorage.setItem(key, value);
};

const removeItem = (key: string) => {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(key);
};

export const getAccessToken = () => getItem(STORAGE_KEYS.ACCESS_TOKEN);
export const setAccessToken = (token: string) => setItem(STORAGE_KEYS.ACCESS_TOKEN, token);
export const removeAccessToken = () => removeItem(STORAGE_KEYS.ACCESS_TOKEN);

export const getRefreshToken = () => getItem(STORAGE_KEYS.REFRESH_TOKEN);
export const setRefreshToken = (token: string) => setItem(STORAGE_KEYS.REFRESH_TOKEN, token);

export const setIdToken = (token: string) => setItem(STORAGE_KEYS.ID_TOKEN, token);

export const getLoginMethod = (): LoginMethod | null => getItem(STORAGE_KEYS.LOGIN_METHOD) as LoginMethod | null;
export const setLoginMethod = (method: LoginMethod) => setItem(STORAGE_KEYS.LOGIN_METHOD, method);
export const removeLoginMethod = () => removeItem(STORAGE_KEYS.LOGIN_METHOD);

export { isEmbedMode } from '@/lib/utils/iframe';

export const setAuthInProgress = (inProgress: boolean) => {
  if (inProgress) setItem(STORAGE_KEYS.AUTH_IN_PROGRESS, 'true');
  else removeItem(STORAGE_KEYS.AUTH_IN_PROGRESS);
};

export const getUserId = () => getItem(STORAGE_KEYS.USER_ID);
export const setUserId = (id: string, source: 'token' | 'profile' = 'token') => {
  setItem(STORAGE_KEYS.USER_ID, id);
  setItem(STORAGE_KEYS.USER_ID_SOURCE, source);
};
// gloss
export const getConfirmedUserId = () =>
  getItem(STORAGE_KEYS.USER_ID_SOURCE) === 'profile' ? getItem(STORAGE_KEYS.USER_ID) : null;
// gloss
// why: no fallback to the stored userID. That key is the brand-scoped alias `<email>_<brandUuid>` for
// why: anyone past their first brand, and the embed token path stores only it — so the fallback
// why: handed the alias to callers that compare or display an address, and EmbedAuthGuard cleared the
// why: session on every mount. useProfile writes the real email from /me, so an empty read heals.
export const getUserEmail = () => getItem(STORAGE_KEYS.USER_EMAIL);
export const setUserEmail = (email: string) => setItem(STORAGE_KEYS.USER_EMAIL, email);
export const getBrandFromStorage = () => getItem(STORAGE_KEYS.BRAND);

// gloss
export const storeTokenResponse = (data: { access_token: string; refresh_token?: string; id_token?: string }) => {
  clearBrandSignedOut(typeof window === 'undefined' ? null : (getHubBrand().uuid ?? null));
  setAccessToken(data.access_token);
  if (data.refresh_token) setRefreshToken(data.refresh_token);
  if (data.id_token) setIdToken(data.id_token);
  notifyTokenChanged();
};

// gloss
export const clearAuthTokens = () => {
  const authKeys = [
    STORAGE_KEYS.ACCESS_TOKEN,
    STORAGE_KEYS.REFRESH_TOKEN,
    STORAGE_KEYS.ID_TOKEN,
    STORAGE_KEYS.LOGIN_METHOD,
    STORAGE_KEYS.USER_ID,
    STORAGE_KEYS.USER_EMAIL,
    STORAGE_KEYS.USER_ID_SOURCE,
    STORAGE_KEYS.USER_UUID,
    STORAGE_KEYS.AUTH_IN_PROGRESS,
  ];
  authKeys.forEach(removeItem);
};

// gloss
export const notifyTokenChanged = () => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(AUTH_TOKEN_CHANGED_EVENT));
};
